import { useEffect, useId, useRef } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useFocusTrap } from "@/lib/useFocusTrap";

/**
 * FEAT-18 — the code editor's help panel, opened from the editor toolbar.
 *
 * It opens IN the page and links nowhere: a help page in a new tab is a tab
 * switch, and on a monitored test that is recorded against the candidate.
 *
 * `intro` is the workspace's own "How the code editor works" text (Settings →
 * Completion, FEAT-22) or the platform default — the text the candidate read
 * on the start page. It is PLAIN TEXT rendered as React text nodes, never
 * HTML or Markdown, so a recruiter-authored string cannot carry markup here.
 *
 * Built like the other candidate dialogs: no portal (the candidate subtree is
 * `translate="no"`, App.tsx), and Escape closes it but is never advertised —
 * in a real test Escape also leaves fullscreen, and one exit flags the
 * attempt. The listener is bubble-phase and prevents nothing, so the
 * fullscreen hook's own capture-phase handler still sees the key.
 *
 * All wording here is a placeholder (Emilia's).
 */

/** Shown when there is no workspace text to show (the recruiter's Preview, or
 *  the text could not be fetched) — the backend's default, in the same words
 *  (`backend/branding.py: DEFAULT_CODE_EDITOR_INSTRUCTIONS`). */
export const DEFAULT_EDITOR_HELP_INTRO =
  "Coding questions open in a code editor in your browser — there is nothing to install.\n\n" +
  "Write your solution in the editor, then press Run tests to check it against the visible " +
  "tests before you move on. Your final code may also be checked against tests you cannot " +
  "see, so make sure it handles more than the visible ones.\n\n" +
  "The programming language is set for each question and cannot be changed.";

interface EditorHelpDialogProps {
  intro?: string | null;
  /** The toolbar offers Reset on this question. */
  canReset: boolean;
  /** Activity monitoring is on for this test (never true in Practice or the
   *  recruiter's Preview). */
  monitored: boolean;
  /** FEAT-31: this attempt stores a code replay — say so. Never set by
   *  Practice or the recruiter's Preview. */
  replayRecorded?: boolean;
  /** FEAT-29: this question shows the "Your tests" card. */
  ownTests?: boolean;
  onClose: () => void;
}

function isApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || "");
}

function Key({ children }: { children: string }) {
  return (
    <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-3xs text-foreground">
      {children}
    </kbd>
  );
}

function Keys({ combo }: { combo: string[] }) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      {combo.map((k, i) => (
        <Key key={k + i}>{k}</Key>
      ))}
    </span>
  );
}

export function EditorHelpDialog({
  intro,
  canReset,
  monitored,
  replayRecorded = false,
  ownTests = false,
  onClose,
}: EditorHelpDialogProps) {
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(cardRef, true);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const paragraphs = ((intro ?? "").trim() || DEFAULT_EDITOR_HELP_INTRO)
    .split(/\n[ \t]*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  const mod = isApplePlatform() ? "⌘" : "Ctrl";
  const shortcuts: Array<{ label: string; combo: string[] }> = [
    { label: "Undo", combo: [mod, "Z"] },
    { label: "Redo", combo: [mod, "Shift", "Z"] },
    { label: "Comment or uncomment a line", combo: [mod, "/"] },
    { label: "Find in your code", combo: [mod, "F"] },
    { label: "Indent the selected lines", combo: ["Tab"] },
    { label: "Outdent the selected lines", combo: ["Shift", "Tab"] },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="editor-help"
      onClick={onClose}
    >
      <div
        ref={cardRef}
        className="max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* The X is the FIRST focusable element on purpose: the focus trap
            focuses the first one on open, and with only the bottom "Close" a
            short screen opened the panel scrolled to its end. */}
        <div className="flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-lg font-semibold tracking-tight">
            Code editor help
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close help"
            className="-mr-1 -mt-1 shrink-0 cursor-pointer rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <section className="mt-4 space-y-2" data-testid="editor-help-intro">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            How the code editor works
          </h3>
          {paragraphs.map((paragraph, i) => (
            <p key={i} className="whitespace-pre-line text-sm text-foreground">
              {paragraph}
            </p>
          ))}
        </section>

        <section className="mt-5 space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            The buttons
          </h3>
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="font-medium text-foreground">Run tests</dt>
              <dd className="text-muted-foreground">
                Checks your code against the example tests next to the
                question. You can run it as often as you like; the clock keeps
                running.
              </dd>
            </div>
            {ownTests && (
              <div data-testid="editor-help-own-tests">
                <dt className="font-medium text-foreground">Your tests</dt>
                <dd className="text-muted-foreground">
                  Under the example tests you can add up to five test cases of
                  your own. They run with Run tests, show you what your code
                  returns, and are never graded.
                </dd>
              </div>
            )}
            {canReset && (
              <div>
                <dt className="font-medium text-foreground">Reset</dt>
                <dd className="text-muted-foreground">
                  Puts back the code this question started with. Undo brings
                  your own code back while you stay on this question.
                </dd>
              </div>
            )}
            <div>
              <dt className="font-medium text-foreground">Settings</dt>
              <dd className="text-muted-foreground">
                Text size, a dark or light editor, and wrapping of long lines.
                Your choices stay for the rest of this test.
              </dd>
            </div>
          </dl>
        </section>

        <section className="mt-5 space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Keyboard shortcuts
          </h3>
          <ul className="space-y-1.5 text-sm">
            {shortcuts.map((s) => (
              <li key={s.label} className="flex items-center justify-between gap-3">
                <span className="text-foreground">{s.label}</span>
                <Keys combo={s.combo} />
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">
            Copy, cut and paste work inside the editor
            {ownTests ? " and in your own tests" : ""}.
          </p>
        </section>

        <section className="mt-5 space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Good to know
          </h3>
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
            <li>
              Your code is saved when you move to another question and when the
              time runs out. Reloading the page brings back your last saved
              code.
            </li>
            {monitored && (
              <li data-testid="editor-help-monitored">
                This test is monitored. The browser&rsquo;s own shortcuts for
                saving, printing and viewing the page source are blocked and
                recorded, and so is anything pasted into the editor
                {ownTests ? " or into your own tests" : ""}.
              </li>
            )}
            {replayRecorded && (
              // Placeholder copy (wording is Emilia's). What is stored, and
              // for how long, is the privacy notice's job — not restated here.
              <li data-testid="editor-help-replay">
                How your code changes while you write it is recorded and can
                be replayed by the company that invited you &mdash; including
                text you paste or delete in the editor. It is the text of the
                editor, not a recording of your screen.
              </li>
            )}
          </ul>
        </section>

        <div className="mt-6 flex justify-end">
          <Button type="button" onClick={onClose} className="cursor-pointer">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
